import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  CalendarIcon,
  Download,
  Printer,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Info,
} from "lucide-react";
import { format, startOfWeek, endOfWeek, addWeeks } from "date-fns";
import { fr } from "date-fns/locale";
import {
  getOfficialDistribution,
  compareDistribution,
  getActualDistribution,
} from "../../lib/api";
import { MATIERES, MATIERE_TO_DISCIPLINE } from "./constants";

export default function HourlyDistributionDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState("cycle2");
  const [period, setPeriod] = useState("week");
  const [startDate, setStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [comparison, setComparison] = useState(null);
  const [official, setOfficial] = useState(null);

  // Calculate end date based on period
  const getEndDate = useCallback(() => {
    switch (period) {
      case "week":
        return endOfWeek(startDate, { weekStartsOn: 1 });
      case "period":
        return addWeeks(startDate, 6);
      case "year":
        return addWeeks(startDate, 36);
      default:
        return endOfWeek(startDate, { weekStartsOn: 1 });
    }
  }, [period, startDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(getEndDate(), "yyyy-MM-dd");

      const [officialRes, comparisonRes] = await Promise.all([
        getOfficialDistribution(cycle),
        compareDistribution(cycle, startStr, endStr),
      ]);

      setOfficial(officialRes.data);
      setComparison(comparisonRes.data);
    } catch (error) {
      console.error("Error fetching distribution:", error);
    } finally {
      setLoading(false);
    }
  }, [cycle, startDate, getEndDate]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "ok":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "insufficient":
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case "excess":
        return <TrendingUp className="w-4 h-4 text-amber-500" />;
      default:
        return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "ok":
        return "bg-green-50 border-green-200";
      case "insufficient":
        return "bg-red-50 border-red-200";
      case "excess":
        return "bg-amber-50 border-amber-200";
      default:
        return "bg-slate-50 border-slate-200";
    }
  };

  const getDisciplineColor = (discipline) => {
    const matiere = MATIERES.find(m => 
      MATIERE_TO_DISCIPLINE[m.value] === discipline || m.label === discipline
    );
    return matiere?.color || "#64748B";
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-indigo-500" />
            Répartition horaire
          </DialogTitle>
          <DialogDescription>
            Comparez le temps prévu par discipline avec les recommandations officielles
          </DialogDescription>
        </DialogHeader>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-center border-b pb-4">
          <Select value={cycle} onValueChange={setCycle}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cycle1">Cycle 1 - Maternelle</SelectItem>
              <SelectItem value="cycle2">Cycle 2 - CP/CE1/CE2</SelectItem>
              <SelectItem value="cycle3">Cycle 3 - CM1/CM2/6e</SelectItem>
            </SelectContent>
          </Select>

          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Semaine</SelectItem>
              <SelectItem value="period">Période (6 sem.)</SelectItem>
              <SelectItem value="year">Année (36 sem.)</SelectItem>
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-48">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(startDate, "d MMM yyyy", { locale: fr })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(startOfWeek(date, { weekStartsOn: 1 }))}
                locale={fr}
              />
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>

          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : comparison ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-xl p-4 border">
                <div className="text-sm text-slate-500">Période analysée</div>
                <div className="text-lg font-semibold">
                  {comparison.period.weeks} semaine{comparison.period.weeks > 1 ? "s" : ""}
                </div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <div className="text-sm text-green-600">Conforme</div>
                <div className="text-lg font-semibold text-green-700">
                  {comparison.comparison.filter(c => c.status === "ok").length} disciplines
                </div>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <div className="text-sm text-red-600">À ajuster</div>
                <div className="text-lg font-semibold text-red-700">
                  {comparison.comparison.filter(c => c.status !== "ok").length} disciplines
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-3 text-sm font-semibold text-slate-600">
                      Discipline
                    </th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600 w-28">
                      Attendu/sem.
                    </th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600 w-28">
                      Réalisé
                    </th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600 w-28">
                      Écart
                    </th>
                    <th className="text-center p-3 text-sm font-semibold text-slate-600 w-24">
                      Statut
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.comparison.map((item, index) => (
                    <tr
                      key={item.discipline}
                      className={`border-b last:border-b-0 ${getStatusBg(item.status)}`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getDisciplineColor(item.discipline) }}
                          />
                          <span className="font-medium text-slate-800">
                            {item.discipline}
                          </span>
                        </div>
                      </td>
                      <td className="text-center p-3">
                        <span className="text-slate-600 font-mono">
                          {item.official_weekly}
                        </span>
                      </td>
                      <td className="text-center p-3">
                        <span className="text-slate-800 font-mono font-semibold">
                          {item.actual_formatted}
                        </span>
                      </td>
                      <td className="text-center p-3">
                        <span
                          className={`font-mono font-medium ${
                            item.diff_percent > 0
                              ? "text-amber-600"
                              : item.diff_percent < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {item.diff_percent > 0 ? "+" : ""}
                          {item.diff_percent}%
                        </span>
                      </td>
                      <td className="text-center p-3">
                        <div className="flex items-center justify-center gap-1">
                          {getStatusIcon(item.status)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Conforme (±10%)</span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <span>Insuffisant (&lt;-10%)</span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-amber-500" />
                <span>Excédent (&gt;+10%)</span>
              </div>
            </div>

            {/* Alerts */}
            {comparison.comparison.some(c => c.status === "insufficient") && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <div className="font-semibold text-red-800">
                      Attention : temps insuffisant
                    </div>
                    <div className="text-sm text-red-600 mt-1">
                      Les disciplines suivantes sont en dessous des recommandations :{" "}
                      {comparison.comparison
                        .filter(c => c.status === "insufficient")
                        .map(c => c.discipline)
                        .join(", ")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            Aucune donnée disponible
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
